import { useEffect, useState } from "react";
import {
  Button,
  FileUploaderButton,
  InlineLoading,
  Select,
  SelectItem,
} from "@carbon/react";
import { Close } from "@carbon/icons-react";
import i18next from "../../../i18n";
import { FileType } from "../../../utils";
import { OrderFile } from "../../../types";

const addFilesOptions = [
  { id: 1, name: i18next.t("tenders.form.plication"), type: FileType.SHEET },
  {
    id: 2,
    name: i18next.t("tenders.form.certificate"),
    type: FileType.CERTIFICATION,
  },
  {
    id: 3,
    name: i18next.t("tenders.form.additionalDocument"),
    type: FileType.ATTACHMENT,
  },
  { id: 4, name: i18next.t("tenders.form.brief"), type: FileType.BRIEF },
  {
    id: 5,
    name: i18next.t("tenders.form.servicePlace"),
    type: FileType.SERVICE_PLACE,
  },
];

type GeneralDataFilesProps = {
  files: OrderFile[];
  isLoadingUpload: boolean;
  isUploadSuccess: boolean;
  uploadedFileUrl: string | null;
  handleSpecification: (event: any) => void;
  onAddFile: (file: any) => void;
  onRemoveFile: (index: number) => void;
};

/** "Add files" block of the general-data step: pick a document type, upload,
 * list + remove. Owns the transient upload bookkeeping. */
const GeneralDataFiles = ({
  files,
  isLoadingUpload,
  isUploadSuccess,
  uploadedFileUrl,
  handleSpecification,
  onAddFile,
  onRemoveFile,
}: GeneralDataFilesProps) => {
  const [selectedFileType, setSelectedFileType] = useState<{
    name?: string;
    fileName?: string;
    type?: string;
  }>({ name: "", fileName: "", type: "" });
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [pendingUploadId, setPendingUploadId] = useState<number | null>(null);

  const handleFileUpload = (event: any) => {
    const file = event?.target?.files;
    if (file) {
      setSelectedFileType((prevState) => ({
        ...prevState,
        fileName: file[0].name,
      }));
      setPendingUploadId(Date.now());
    }
    handleSpecification(event);
    setFileInputKey(Date.now());
  };

  useEffect(() => {
    if (
      uploadedFileUrl &&
      pendingUploadId &&
      selectedFileType?.name &&
      selectedFileType?.fileName &&
      isUploadSuccess &&
      !isLoadingUpload
    ) {
      onAddFile({
        fileName: selectedFileType?.fileName,
        name: selectedFileType?.name,
        fileURL: uploadedFileUrl,
        type: selectedFileType.type,
      });
      setSelectedFileType({ name: "", fileName: "", type: "" });
      setPendingUploadId(null);
    }
  }, [
    uploadedFileUrl,
    selectedFileType?.fileName,
    selectedFileType?.name,
    selectedFileType.type,
    onAddFile,
    pendingUploadId,
    isUploadSuccess,
    isLoadingUpload,
  ]);

  return (
    <div className="order-col-full order-general-files">
      <div>
        <Select
          id="order-file-type"
          labelText={i18next.t("cta.addFiles")}
          value={selectedFileType?.name}
          onChange={(e) =>
            setSelectedFileType({
              name: e.target.value,
              type: addFilesOptions.find((opt) => opt.name === e.target.value)
                ?.type,
            })
          }
        >
          <SelectItem value="" text={i18next.t("tenders.form.selectFileType")} />
          {addFilesOptions.map((type) => (
            <SelectItem key={type.id} value={type.name} text={type.name} />
          ))}
        </Select>
        {selectedFileType?.name &&
          (isLoadingUpload ? (
            <InlineLoading description={i18next.t("cta.attach")} />
          ) : (
            <FileUploaderButton
              key={fileInputKey}
              buttonKind="tertiary"
              labelText={`${i18next.t("cta.attach")} ${selectedFileType?.name}`}
              disableLabelChanges
              accept={[".pdf"]}
              disabled={isLoadingUpload || selectedFileType?.name === ""}
              onChange={handleFileUpload}
            />
          ))}
      </div>

      <div>
        {files.map((file: OrderFile, index: number) => (
          <div key={file.fileURL} className="order-file-row">
            <span>
              {file.name}
              {file.fileName && ` - ${file.fileName}`}
            </span>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Close}
              iconDescription={i18next.t("cta.remove")}
              onClick={() => onRemoveFile(index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeneralDataFiles;
